import clone from 'ramda/src/clone';
import countBy from 'ramda/src/countBy';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import findLast from 'ramda/src/findLast';
import isNil from 'ramda/src/isNil';
import pathOr from 'ramda/src/pathOr';
import pipe from 'ramda/src/pipe';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import sortBy from 'ramda/src/sortBy';
import equals from 'ramda/src/equals';
import { createBillingAddressString } from 'invision-core/src/utilities/address.helper';
import i18n from 'invision-core/src/components/i18n/i18n';
import { convertStringToNumber } from 'invision-core/src/components/helpers/conversion.helper';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { PaymentInstrumentTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors.deprecated';
import { hasAccess } from 'invision-core/src/components/security/permission.service';
import { UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
import LocaleKeys from '../../locales/keys';
import Immutable from 'seamless-immutable';
import { createSelector } from 'reselect';
import { SelectedCustomerSelector } from './customer.selectors';
import { formatCardName, isExpired } from '../../utilities/credit.card.helper';
import { getDefaultPaymentMethodFromPaymentInstruments, getDefaultPaymentMethodFromPaymentInstrumentsWithAutoPay, isCreatablePaymentInstrumentType, isAutoPaySupportedPaymentInstrumentType } from '../helpers/ewallet.reducer.helper';
import { SUPPORTED_OTT_PAYMENT_INSTRUMENT_TYPES, SUPPORTED_PAYMENT_INSTRUMENT_TYPES } from '../../components/customer/ewallet/ewallet.constants';
import { getFormattedExpirationForCard } from '../../components/customer/ewallet/ewallet.helpers';
import { parseAdditionalProperties, PaymentInstrumentConfigurationAdditionalProperties, paymentInstrumentConfigurationAdditionalPropertyDescriptors, PaymentInstrumentTypeAdditionalProperties, paymentInstrumentTypeAdditionalPropertyDescriptors } from '../helpers/payment.instrument.additional.property.parser';
import { E_WALLET_ADMIN_ACCESS } from '../../security.attributes';
import { hasPrepaidBalance } from '../../utilities/payment.instrument.helper';
import * as SelectorHelper from './customer.ewallet.selectors.helpers';
import { MakePaymentInstrumentSelector } from './../../components/customer/makePayment/make.payment.selectors';
import { PaymentInfoEditSelector } from './new.connect.wizard.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { isPaymentInstrumentTypeValueOptionSelected } from './modify.payment.method.popup.selectors.helpers';
var CONVERGENT_BILLER_INVOICE = Number(SUPPORTED_OTT_PAYMENT_INSTRUMENT_TYPES.CONVERGENT_BILLER_INVOICE);
var EMPTY_ARRAY = [];
var eWalletSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.eWallet;
});
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.ewallet;
};
var eWalletDataSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.data;
});
export var PaymentInstrumentsSelector = createSelector([eWalletDataSelector], function (eWalletData) {
  return eWalletData.paymentInstruments ? eWalletData.paymentInstruments.map(function (paymentInstrument) {
    var metaLine;
    if (paymentInstrument.Type === parseInt(SUPPORTED_PAYMENT_INSTRUMENT_TYPES.CREDIT_CARD, 10)) {
      var shortDate = getFormattedExpirationForCard(paymentInstrument.CreditCard);
      metaLine = i18n.translate(LocaleKeys.HOUSEHOLD.PRIVILEGES.EXPIRES_ON, {
        shortDate: shortDate
      });
    } else {
      metaLine = paymentInstrument.TypeName;
    }
    return paymentInstrument.set('metaLine', metaLine);
  }) : null;
});
export var MutablePaymentInstrumentsSelector = createSelector([PaymentInstrumentsSelector], function (paymentInstruments) {
  return paymentInstruments ? paymentInstruments.asMutable({
    deep: true
  }) : EMPTY_ARRAY;
});
export var PaymentTypesSelector = createSelector([MetadataCodeTypeSelector(CODES.CreditCardType), MetadataCodeTypeSelector(CODES.ExternalBillType), MetadataCodeTypeSelector(CODES.ExternalGiftCardType), MetadataCodeTypeSelector(CODES.PaymentInstrumentType)], function (creditCardTypes, externalBillTypes, externalGiftCardTypes, paymentInstrumentTypes) {
  var allTypes = [].concat(creditCardTypes, externalBillTypes, externalGiftCardTypes, paymentInstrumentTypes);
  return {
    allTypes: allTypes,
    creditCardTypes: creditCardTypes,
    externalBillTypes: externalBillTypes,
    externalGiftCardTypes: externalGiftCardTypes,
    paymentInstrumentTypes: paymentInstrumentTypes
  };
});
export var ActionablesPerPaymentTypeSelector = createSelector([MetadataCodeTypeSelector(CODES.PaymentInstrumentType)], function (paymentInstrumentTypes) {
  return (paymentInstrumentTypes || []).reduce(function (lookup, paymentType) {
    var typeId = paymentType.Value;
    var model = new PaymentInstrumentTypeAdditionalProperties();
    lookup[typeId] = parseAdditionalProperties(model, paymentType.AdditionalProperties, paymentInstrumentTypeAdditionalPropertyDescriptors);
    return lookup;
  }, {});
});
export var PaymentInstrumentsWithTypeNamesViewModel = createSelector([PaymentInstrumentsSelector, PaymentTypesSelector, ActionablesPerPaymentTypeSelector], function (paymentInstruments, paymentTypes, actionablesPerPermissionType) {
  if (!paymentInstruments) {
    return [];
  }
  return [].concat(paymentInstruments).map(function (customerPaymentInstrument) {
    return SelectorHelper.paymentInstrumentToViewModel(customerPaymentInstrument, paymentTypes, actionablesPerPermissionType);
  }).sort(SelectorHelper.comparePaymentInstrumentViewModelByTypeAndCreation);
});
export var PaymentInstrumentsCanBeUsedForOrderingWithTypeNamesViewModelSelector = createSelector(PaymentInstrumentsWithTypeNamesViewModel, function (paymentInstruments) {
  if (!paymentInstruments) {
    return [];
  }
  var result = [].concat(paymentInstruments).filter(function (paymentInstrument) {
    return paymentInstrument.actionables.canOrder;
  }).sort(SelectorHelper.comparePaymentInstrumentViewModelByTypeAndCreation);
  return result;
});
export var PaymentInstrumentsCanBeUsedForOrderingWithTypeNamesAutoPayViewModelSelector = createSelector(PaymentInstrumentsWithTypeNamesViewModel, function (paymentInstruments) {
  if (!paymentInstruments) {
    return [];
  }
  return [].concat(paymentInstruments).filter(function (paymentInstrument) {
    return paymentInstrument.Type === convertStringToNumber(SUPPORTED_PAYMENT_INSTRUMENT_TYPES.CURRENCY_ENTITLEMENT) || paymentInstrument.actionables.canOrder && isAutoPaySupportedPaymentInstrumentType(paymentInstrument.Type);
  }).sort(SelectorHelper.comparePaymentInstrumentViewModelByTypeAndCreation);
});
export var PaymentInstrumentConfigurationSelector = createSelector([MetadataCodeTypeSelector(CODES.PaymentInstrumentConfiguration)], function (paymentInstrumentConfigurationAdditionalProperties) {
  return (paymentInstrumentConfigurationAdditionalProperties || []).map(function (paymentConfig) {
    var model = new PaymentInstrumentConfigurationAdditionalProperties();
    var additionalProperties = parseAdditionalProperties(model, paymentConfig.AdditionalProperties, paymentInstrumentConfigurationAdditionalPropertyDescriptors);
    return additionalProperties;
  });
});
export var AvailableTypesMapSelector = createSelector([eWalletDataSelector], function (eWalletData) {
  //This selector should return null if null is what it has to return.
  //PLEASE DO NOT update this selector to return an EMPTY OBJECT if null because
  //null means it hasn't been set and EMPTY OBJECT means that all payment types (non-removed) are available for use on this distribution channel
  return eWalletData.availableTypesMap;
});
export var IsAllPaymentInstrumentTypesAvailableSelector = createImmutableSelector([AvailableTypesMapSelector], function (availableTypes) {
  //If availableTypesMap is an empty object then it means that all payment
  //instrument types (non-removed) are available for use on this distribution channel
  return equals(availableTypes, {});
});
export var PaymentInstrumentTypeValueOptions = createImmutableSelector([PaymentInstrumentTypeSelector, ActionablesPerPaymentTypeSelector, PaymentInfoEditSelector, AvailableTypesMapSelector, IsAllPaymentInstrumentTypesAvailableSelector], function (paymentInstrumentTypes, actionablesPerPaymentType, paymentInfoEdit, availablePaymentTypesMap, isAllPaymentInstrumentTypesAvailable) {
  var acceptedPaymentInstruments = paymentInstrumentTypes.filter(function (paymentInstrumentType) {
    var actionables = actionablesPerPaymentType[paymentInstrumentType.Value];
    var allowCreate = actionables ? actionables.userCreatable : false;
    var isNotOptedOut = isCreatablePaymentInstrumentType(paymentInstrumentType.Value);
    return allowCreate && isNotOptedOut && (isAllPaymentInstrumentTypesAvailable || !!pathOr(null, ["".concat(paymentInstrumentType.Value)], availablePaymentTypesMap));
  });
  return acceptedPaymentInstruments.map(function (paymentInstrumentType) {
    return {
      text: paymentInstrumentType.Name,
      selected: isPaymentInstrumentTypeValueOptionSelected((paymentInfoEdit || {}).Type, paymentInstrumentType, acceptedPaymentInstruments[0].Value),
      paymentInstrumentType: paymentInstrumentType
    };
  });
});
export var PaymentInstrumentTypeValueOptionsWithAutoPay = createSelector([PaymentInstrumentsSelector, PaymentInstrumentTypeSelector, PaymentInstrumentConfigurationSelector, ActionablesPerPaymentTypeSelector, PaymentInfoEditSelector, AvailableTypesMapSelector, IsAllPaymentInstrumentTypesAvailableSelector], function (paymentInstruments, paymentInstrumentTypes, paymentInstrumentConfigurations, actionablesPerPaymentType, paymentInfoEdit, availablePaymentTypesMap, isAllPaymentInstrumentTypesAvailable) {
  var paymentInstrumentTypeCounts = countBy(prop('Type'))(paymentInstruments);
  var activePaymentInstrumentTypeCounts = countBy(prop('Type'))(filter(propEq(1, 'Status'))(paymentInstruments));
  var acceptedPaymentInstruments = paymentInstrumentTypes.filter(function (paymentInstrumentType) {
    var typeCount = paymentInstrumentTypeCounts[paymentInstrumentType.Value];
    var typeActiveCount = activePaymentInstrumentTypeCounts[paymentInstrumentType.Value];
    var typeConfiguration = pipe(sortBy(prop('Global')), findLast(propEq(paymentInstrumentType.Value, 'paymentInstrumentTypeCode')))(paymentInstrumentConfigurations);
    var isMax = typeConfiguration && typeConfiguration.alertMax > 0 && typeCount >= typeConfiguration.alertMax;
    var isMaxActive = typeConfiguration && typeConfiguration.alertMaxActive > 0 && typeActiveCount >= typeConfiguration.alertMaxActive;
    var actionables = actionablesPerPaymentType[paymentInstrumentType.Value];
    var allowCreate = actionables ? actionables.userCreatable : false;
    var isAutoPay = typeConfiguration ? typeConfiguration.supportsAutoPay : false;
    var isNotOptedOut = isCreatablePaymentInstrumentType(paymentInstrumentType.Value);
    return isAutoPay && allowCreate && isNotOptedOut && !(isMax || isMaxActive) && (isAllPaymentInstrumentTypesAvailable || !!pathOr(null, ["".concat(paymentInstrumentType.Value)], availablePaymentTypesMap));
  });
  return acceptedPaymentInstruments.map(function (paymentInstrumentType) {
    return {
      text: paymentInstrumentType.Name,
      selected: isPaymentInstrumentTypeValueOptionSelected((paymentInfoEdit || {}).Type, paymentInstrumentType, acceptedPaymentInstruments[0].Value),
      paymentInstrumentType: paymentInstrumentType
    };
  });
});

/**
 * Returns Payment Instruments with positive prepaid balance and others that don't track prepaid balance.
 * @type {Reselect.Selector<any, any>}
 * @returns Array<PAYMENT_INSTRUMENT_SUMMARY>
 * @see PaymentInstrumentsCanBeUsedForOrderingWithTypeNamesViewModelSelector
 */
export var PaymentInstrumentsCanBeUsedForOrderingContainingBalanceSelector = createSelector([PaymentInstrumentsCanBeUsedForOrderingWithTypeNamesViewModelSelector], function (paymentInstruments) {
  return paymentInstruments.filter(function (paymentInstrument) {
    return hasPrepaidBalance(paymentInstrument) && !isExpired(paymentInstrument);
  });
});
export var ShareablePaymentInstrumentsWithTypeNamesViewModel = createSelector([PaymentInstrumentsWithTypeNamesViewModel], function (paymentInstruments) {
  if (!paymentInstruments) {
    return [];
  }
  return paymentInstruments.filter(function (paymentInstrument) {
    return paymentInstrument.actionables.isShareable;
  });
});
export var PaymentInstrumentsWithoutBillToInvoiceSelector = createSelector([PaymentInstrumentsCanBeUsedForOrderingContainingBalanceSelector], function (paymentInstruments) {
  return paymentInstruments && paymentInstruments.length ? paymentInstruments.filter(function (instrument) {
    return instrument.Type !== CONVERGENT_BILLER_INVOICE;
  }) : [];
});
export var SelectedItemIdSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.selectedItemId;
});
export var SelectedTypeIdSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.selectedTypeId;
});
export var DefaultPaymentMethodSelector = createSelector([PaymentInstrumentsSelector], function (paymentInstruments) {
  if (paymentInstruments && paymentInstruments.length) {
    return getDefaultPaymentMethodFromPaymentInstruments(paymentInstruments);
  }
  return null;
});
export var DefaultPaymentMethodAutoPaySelector = createSelector([PaymentInstrumentsSelector], function (paymentInstruments) {
  if (paymentInstruments && paymentInstruments.length) {
    return getDefaultPaymentMethodFromPaymentInstrumentsWithAutoPay(paymentInstruments);
  }
  return null;
});
export var SelectedPaymentMethodSelector = createSelector([PaymentInstrumentsSelector, SelectedCustomerSelector], function (paymentInstruments, selectedCustomer) {
  var selectedPayment = pathOr(null, ['eWallet', 'data', 'selectedPaymentInstrument'], selectedCustomer);
  var selectedPaymentId = pathOr(null, ['eWallet', 'data', 'selectedPaymentInstrument', 'Id'], selectedCustomer);
  if (!isNil(selectedPaymentId)) {
    var matchingInstrument = paymentInstruments && paymentInstruments.length && find(propEq(selectedPaymentId, 'Id'))(paymentInstruments);
    if (matchingInstrument) {
      return matchingInstrument;
    } else if (selectedPayment) {
      return selectedPayment;
    }
  }
  return null;
});
export var AutoPayFlagStatusSelector = createSelector([eWalletSelector], function (eWalletData) {
  return eWalletData.isAutoPayActive;
});
export var PaymentMethodWithConvergentBillerAccountsSelector = createSelector([DefaultPaymentMethodAutoPaySelector, SelectedPaymentMethodSelector, AutoPayFlagStatusSelector], function (defaultConvergentAccount, selectedPaymentInstrument, autoPayFlagStatus) {
  if (defaultConvergentAccount && selectedPaymentInstrument) {
    var selectedPayment = clone(selectedPaymentInstrument);
    if (selectedPayment.ConvergentBillerPaymentInstrumentAccounts && selectedPayment.ConvergentBillerPaymentInstrumentAccounts.length) {
      selectedPayment.ConvergentBillerPaymentInstrumentAccounts[0].AutoPay = autoPayFlagStatus;
    } else if (defaultConvergentAccount && defaultConvergentAccount.ConvergentBillerPaymentInstrumentAccounts.length) {
      selectedPayment.ConvergentBillerPaymentInstrumentAccounts = clone(defaultConvergentAccount.ConvergentBillerPaymentInstrumentAccounts);
      selectedPayment.ConvergentBillerPaymentInstrumentAccounts.AutoPay = autoPayFlagStatus;
    } else {
      selectedPayment.ConvergentBillerPaymentInstrumentAccounts = [{
        AutoPay: autoPayFlagStatus
      }];
    }
    return selectedPayment;
  }
  return defaultConvergentAccount;
});
export var SelectedPaymentMethodWithTypeNamesViewModelSelector = createSelector([SelectedPaymentMethodSelector, PaymentTypesSelector, ActionablesPerPaymentTypeSelector], function (selectedPayment, paymentTypes, actionablesPerPermissionType) {
  if (!selectedPayment || !selectedPayment.Id) {
    return selectedPayment;
  }
  return SelectorHelper.paymentInstrumentToViewModel(selectedPayment, paymentTypes, actionablesPerPermissionType);
});
export var NewPaymentMethodWithTypeNamesAndMetadataViewModelSelector = createSelector([MakePaymentInstrumentSelector, PaymentTypesSelector, ActionablesPerPaymentTypeSelector], function (newPaymentInstrument, paymentTypes, actionablesPerPermissionType) {
  if (!newPaymentInstrument || !newPaymentInstrument.Type) {
    return newPaymentInstrument;
  }
  return SelectorHelper.paymentInstrumentToViewModel(newPaymentInstrument, paymentTypes, actionablesPerPermissionType);
});
export var SelectedPaymentMethodConvergentBillerPaymentInstrumentAccountsSelector = createSelector([eWalletDataSelector], function (eWalletData) {
  return eWalletData.convergentBillerAccounts ? eWalletData.convergentBillerAccounts : EMPTY_ARRAY;
});
export var PaymentInstrumentSummariesSelector = createSelector([PaymentInstrumentsSelector, MetadataCodeTypeSelector(CODES.CreditCardType), SelectedPaymentMethodSelector], function (paymentInstruments, creditCardTypes, selectedPaymentMethod) {
  return paymentInstruments.map(function (paymentInstrument) {
    if (!paymentInstrument.BillingAddress || !paymentInstrument.CreditCard) {
      return {};
    }
    var tableRow = {};
    tableRow.paymentInstrument = paymentInstrument;
    tableRow.yourPaymentMethods = formatPaymentCell(paymentInstrument.CreditCard, creditCardTypes);
    tableRow.nameOnCard = paymentInstrument.CreditCard.NameOnCard;
    tableRow.expires = "".concat(paymentInstrument.CreditCard.ExpirationMonth, "/").concat(paymentInstrument.CreditCard.ExpirationYear);
    tableRow.address = createBillingAddressString(paymentInstrument.BillingAddress);
    tableRow.edit = i18n.translate(LocaleKeys.MAKE_PAYMENT.EDIT);
    tableRow.isActive = selectedPaymentMethod ? paymentInstrument.Id === selectedPaymentMethod.Id : false;
    return tableRow;
  });
});
var formatPaymentCell = function formatPaymentCell(creditCard, creditCardTypes) {
  if (!creditCard || !creditCard.AccountNumber) {
    return i18n.translate(LocaleKeys.NO_RESULTS);
  }
  return formatCardName(creditCardTypes, creditCard.Type, creditCard.AccountNumber);
};
export var EwalletErrorSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.eWalletError;
});
export var EwalletErrorsSelector = createSelector([eWalletSelector], function (eWallet) {
  if (eWallet.eWalletError && eWallet.eWalletError.message) {
    return [eWallet.eWalletError.message];
  } else {
    return [];
  }
});
export var EwalletRetrievalErrorSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.eWalletRetrievalError;
});
export var PaymentInstrumentDetailSelector = createSelector([eWalletDataSelector], function (eWalletData) {
  return eWalletData.paymentInstrumentDetail;
});
export var PaymentIsInvalidSelector = createSelector([eWalletDataSelector], function (eWalletData) {
  return eWalletData.paymentIsInvalid;
});
export var PaymentIsCreatingOrEditingDataSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isCreatingOrEditingData;
});
export var PaymentIsRemovingDataSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isRemovingPaymentInstrument;
});
export var PaymentIsUpdatingAutoPaySelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isUpdatingAutoPayConfiguration;
});
export var PaymentIsFetchingDataSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isFetchingData;
});
export var IsRetrievingEwalletDataSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isRetrievingWallet;
});
export var IsShowingPaymentInstrumentPanelSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isShowingPaymentInstrumentPanel;
});
export var IsMakingPaymentSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.isMakingPayment;
});
export var SupplyPaymentInstrumentSelector = createSelector([eWalletSelector], function (eWallet) {
  return eWallet.supplyPaymentInstrument;
});
export var RecordPaymentInformationSelector = createSelector([eWalletSelector], function (eWallet) {
  return Immutable(eWallet.recordPaymentInfo);
});
export var SelectedPaymentForSubmitAtCheckoutSelector = createImmutableSelector([SelectedPaymentMethodSelector, RecordPaymentInformationSelector], function (selectedPayment, recordPaymentInfo) {
  if (selectedPayment) {
    return recordPaymentInfo ? selectedPayment : selectedPayment.without(['Id', 'recordPayment']);
  } else {
    return selectedPayment;
  }
});
export var UserHasEWalletAdminAccess = createSelector([UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return hasAccess(userSecurityAttributes, E_WALLET_ADMIN_ACCESS);
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeRemoved;
});
export var RecoverableUISelectedPaymentMethod = createSelector([PaymentInstrumentsSelector, SelectedItemIdSelector], function (paymentInstruments, selectedItemId) {
  if (!isNil(selectedItemId) && paymentInstruments && paymentInstruments.length) {
    return find(propEq(selectedItemId, 'Id'))(paymentInstruments) || null;
  }
  return null;
});
export var SelectedPaymentModelBasedOnRecoverableUI = createSelector([RecoverableUISelectedPaymentMethod, PaymentTypesSelector, ActionablesPerPaymentTypeSelector], function (selectedPayment, paymentTypes, actionablesPerPermissionType) {
  if (!selectedPayment || !selectedPayment.Id) {
    return selectedPayment;
  }
  return SelectorHelper.paymentInstrumentToViewModel(selectedPayment, paymentTypes, actionablesPerPermissionType);
});
export var SelectedPaymentIsDirectSource = createSelector([SelectedPaymentModelBasedOnRecoverableUI], function (selectedPayment) {
  if (selectedPayment && selectedPayment.actionables) {
    return selectedPayment.actionables.isDirectSource &&
    //PAYPAL requires a Preapproval agreement to be used as a DirectSource
    //until functionality is available to check for this we will block it
    !(String(selectedPayment.Type) === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.PAY_PAL);
  }
  return false;
});
export var SelectedPaymentAutoPayEnable = createSelector([SelectedPaymentMethodSelector], function (selectedPayment) {
  if (selectedPayment && selectedPayment.ConvergentBillerPaymentInstrumentAccounts && selectedPayment.ConvergentBillerPaymentInstrumentAccounts.length) {
    return selectedPayment.ConvergentBillerPaymentInstrumentAccounts[0].AutoPay;
  }
  return false;
});
export var DefaultPaymentAutoPayEnable = createSelector([DefaultPaymentMethodAutoPaySelector], function (defaultPaymentMethodAutoPay) {
  if (defaultPaymentMethodAutoPay && defaultPaymentMethodAutoPay.ConvergentBillerPaymentInstrumentAccounts && defaultPaymentMethodAutoPay.ConvergentBillerPaymentInstrumentAccounts.length) {
    return defaultPaymentMethodAutoPay.ConvergentBillerPaymentInstrumentAccounts[0].AutoPay || false;
  }
  return false;
});
export var CurrentDefaultPaymentModel = createSelector([DefaultPaymentMethodSelector, PaymentTypesSelector, ActionablesPerPaymentTypeSelector], function (defaultPayment, paymentTypes, actionablesPerPermissionType) {
  if (!!defaultPayment && !!defaultPayment.Id) {
    return SelectorHelper.paymentInstrumentToViewModel(defaultPayment, paymentTypes, actionablesPerPermissionType);
  }
  return defaultPayment;
});
export var IsRenewingOffCycleChargesSelector = createImmutableSelector([eWalletSelector], function (eWallet) {
  return eWallet.isRenewingOffCycleCharges;
});